import type {
  PlacementComponentOption,
  PlacementComponentPropDefinition,
} from '~/modules/query/ai/types';

/** The model a new placement starts with; the create form and validator share this one constant. */
export const DEFAULT_MODEL = 'openai-gpt-oss-120b';

const AI_DISCLAIMER_TEXT =
  'Generated using AI. AI can make mistakes, please double check copy before continuing to post.';

/** The label, counter, disclaimer and insert-button props the text field and text area share. */
const textAiPropDefinitions = (defaultLabel: string): PlacementComponentPropDefinition[] => {
  const sampleProp = (
    definition: Omit<PlacementComponentPropDefinition, 'required' | 'source' | 'sourcePath'>,
  ): PlacementComponentPropDefinition => ({
    ...definition,
    required: false,
    source: 'sampleProps',
    sourcePath: definition.name,
  });

  return [
    sampleProp({
      name: 'labelText',
      type: 'string',
      defaultValue: defaultLabel,
      description: 'Field label. Pass an empty string to hide the label row entirely.',
    }),
    sampleProp({
      name: 'labelTooltip',
      type: 'string',
      description: "Shows a help icon next to the label; its content is this tooltip's text.",
    }),
    sampleProp({
      name: 'required',
      type: 'boolean',
      defaultValue: false,
      description: 'Shows a required-field asterisk next to the label.',
    }),
    sampleProp({
      name: 'errorText',
      type: 'string',
      description:
        'Presence switches the field to its error state and shows this message below it.',
    }),
    sampleProp({
      name: 'characterLimit',
      type: 'number',
      description: "Enables the character counter at this limit (also the input's own max length).",
    }),
    sampleProp({
      name: 'tipText',
      type: 'string',
      description: 'Shown in a callout below the field, only while characterLimit is set.',
    }),
    sampleProp({
      name: 'disclaimerText',
      type: 'string',
      defaultValue: AI_DISCLAIMER_TEXT,
      description: 'AI-generated-content disclaimer text shown below the field.',
    }),
    sampleProp({
      name: 'showDisclaimer',
      type: 'boolean',
      defaultValue: true,
      description: 'Hides the AI disclaimer row when set to false.',
    }),
    sampleProp({
      name: 'bracesButton',
      type: 'boolean',
      defaultValue: true,
      description: 'Shows the insert-dynamic-field icon button.',
    }),
    sampleProp({
      name: 'emojiButton',
      type: 'boolean',
      defaultValue: true,
      description: 'Shows the insert-emoji icon button.',
    }),
  ];
};

const TEXT_FIELD_PROP_DEFINITIONS: PlacementComponentPropDefinition[] = [
  {
    name: 'defaultValue',
    type: 'string',
    required: false,
    source: 'sampleProps',
    sourcePath: 'defaultValue (fallback: sampleContext.initialText)',
    defaultValue: '',
    description: 'Initial value shown in the input. Overrides sample context when provided.',
  },
  {
    name: 'placeholder',
    type: 'string',
    required: false,
    source: 'sampleProps',
    sourcePath: 'placeholder',
    defaultValue: 'Type your message...',
    description: 'Placeholder text for an empty input.',
  },
  {
    name: 'isDisabled',
    type: 'boolean',
    required: false,
    source: 'sampleProps',
    sourcePath: 'isDisabled (fallback: runtime.isDisabled)',
    defaultValue: false,
    description: 'Disables the entire component, including editing and AI, for preview overrides.',
  },
  {
    name: 'disabledTooltip',
    type: 'string',
    required: false,
    source: 'sampleProps',
    sourcePath: 'disabledTooltip (fallback: runtime.disabledTooltip)',
    description: 'Tooltip shown while the full component is disabled.',
  },
  {
    name: 'aiDisabled',
    type: 'boolean',
    required: false,
    source: 'sampleProps',
    sourcePath: 'aiDisabled (fallback: runtime.aiDisabled)',
    defaultValue: false,
    description: 'Disables AI actions only while keeping text input editable.',
  },
  {
    name: 'aiDisableTooltip',
    type: 'string',
    required: false,
    source: 'sampleProps',
    sourcePath: 'aiDisableTooltip (fallback: runtime.aiDisableTooltip)',
    description: 'Tooltip shown when AI actions are unavailable.',
  },
  {
    name: 'isRunning',
    type: 'boolean',
    required: false,
    source: 'sampleProps',
    sourcePath: 'isRunning (fallback: runtime.isRunning)',
    defaultValue: false,
    description: 'Shows loading state while a test run is active.',
  },
  ...textAiPropDefinitions('Text field'),
];

const TEXT_AREA_PROP_DEFINITIONS: PlacementComponentPropDefinition[] = [
  {
    name: 'defaultValue',
    type: 'string',
    required: false,
    source: 'sampleContext',
    sourcePath: 'initialText',
    defaultValue: '',
    description: 'Initial value shown in the text area.',
  },
  {
    name: 'placeholder',
    type: 'string',
    required: false,
    source: 'sampleProps',
    sourcePath: 'placeholder',
    defaultValue: 'Type your message...',
    description: 'Placeholder text for an empty text area.',
  },
  {
    name: 'isDisabled',
    type: 'boolean',
    required: false,
    source: 'runtime',
    defaultValue: false,
    description: 'Disables the entire component, including editing and AI.',
  },
  {
    name: 'disabledTooltip',
    type: 'string',
    required: false,
    source: 'runtime',
    description: 'Tooltip shown while the full component is disabled.',
  },
  {
    name: 'aiDisabled',
    type: 'boolean',
    required: false,
    source: 'runtime',
    defaultValue: false,
    description: 'Disables AI actions only while keeping text input editable.',
  },
  {
    name: 'aiDisableTooltip',
    type: 'string',
    required: false,
    source: 'runtime',
    description: 'Tooltip shown when AI actions are unavailable.',
  },
  {
    name: 'isRunning',
    type: 'boolean',
    required: false,
    source: 'runtime',
    defaultValue: false,
    description: 'Shows loading state while a test run is active.',
  },
  ...textAiPropDefinitions('Text area'),
];

const RICH_TEXT_PROP_DEFINITIONS: PlacementComponentPropDefinition[] = [
  {
    name: 'defaultValue',
    type: 'string',
    required: false,
    source: 'sampleContext',
    sourcePath: 'initialHtml (fallback: initialText)',
    defaultValue: '',
    description: 'Initial HTML content shown in the editor.',
  },
  {
    name: 'placeholder',
    type: 'string',
    required: false,
    source: 'sampleProps',
    sourcePath: 'placeholder',
    defaultValue: 'Write your rich text content...',
    description: 'Placeholder text shown when editor content is empty.',
  },
  {
    name: 'isDisabled',
    type: 'boolean',
    required: false,
    source: 'runtime',
    defaultValue: false,
    description: 'Disables the full rich-text editor and AI actions.',
  },
  {
    name: 'disabledTooltip',
    type: 'string',
    required: false,
    source: 'runtime',
    description: 'Tooltip shown while the full component is disabled.',
  },
  {
    name: 'aiDisabled',
    type: 'boolean',
    required: false,
    source: 'runtime',
    defaultValue: false,
    description: 'Disables AI actions only while keeping editor input enabled.',
  },
  {
    name: 'aiDisableTooltip',
    type: 'string',
    required: false,
    source: 'runtime',
    description: 'Tooltip shown when AI actions are unavailable.',
  },
  {
    name: 'isRunning',
    type: 'boolean',
    required: false,
    source: 'runtime',
    defaultValue: false,
    description: 'Shows loading state while a test run is active.',
  },
];

/**
 * The approved component catalogue. service-ai returns placements only, so the
 * editor carries this static list for the create form and render snippets.
 */
export const PLACEMENT_COMPONENTS: PlacementComponentOption[] = [
  {
    id: 'text.field',
    label: 'Text field',
    description:
      'Text field with an AI Write popover, dynamic field and emoji inserts, and a character limit',
    versions: [
      {
        version: '1.0.0',
        status: 'active',
        releaseNote:
          'Text field with an anchored AI Write popover and result history, dynamic field and emoji inserts, character limit, and label, tip and disclaimer props.',
        defaultContext: { initialText: 'Where Dream Homes Meet Real Connections.' },
        defaultProps: {
          defaultValue: '',
          placeholder: 'Type your message...',
          isDisabled: false,
          disabledTooltip: 'Text field is disabled.',
          aiDisabled: false,
          aiDisableTooltip: 'AI actions are unavailable.',
          isRunning: false,
        },
        propDefinitions: TEXT_FIELD_PROP_DEFINITIONS,
        renderSnippet:
          '<TextField placementKey="{{placementKey}}" componentVersion="{{componentVersion}}" />',
      },
    ],
  },
  {
    id: 'text.area',
    label: 'Text area',
    description:
      'Text area with an AI Write popover, dynamic field and emoji inserts, and a character limit',
    versions: [
      {
        version: '1.0.0',
        status: 'active',
        releaseNote:
          'Text area with an anchored AI Write popover and result history, dynamic field and emoji inserts, character limit, and label, tip and disclaimer props.',
        defaultContext: { initialText: 'Where Dream Homes Meet Real Connections.' },
        defaultProps: { placeholder: 'Type your message...' },
        propDefinitions: TEXT_AREA_PROP_DEFINITIONS,
        renderSnippet:
          '<TextArea placementKey="{{placementKey}}" componentVersion="{{componentVersion}}" />',
      },
    ],
  },
  {
    id: 'rich.text',
    label: 'Rich text',
    description: 'Rich text editor with AI HTML writer modal',
    versions: [
      {
        version: '1.0.0',
        status: 'active',
        releaseNote: 'Initial rich text editor with custom AI button and HTML generation modal.',
        defaultContext: { initialText: 'Where Dream Homes Meet Real Connections.' },
        defaultProps: { placeholder: 'Write your rich text content...' },
        propDefinitions: RICH_TEXT_PROP_DEFINITIONS,
        renderSnippet:
          '<RichText placementKey="{{placementKey}}" componentVersion="{{componentVersion}}" />',
      },
    ],
  },
];

export const DEFAULT_PLACEMENT_SAMPLE_CONTEXT = JSON.stringify(
  { initialText: 'Where Dream Homes Meet Real Connections.' },
  null,
  2,
);
export const DEFAULT_PLACEMENT_SAMPLE_PROPS = JSON.stringify(
  { placeholder: 'Type your message...' },
  null,
  2,
);

export interface PlacementComponentVersionLike {
  defaultContext: Record<string, unknown>;
  defaultProps: Record<string, unknown>;
  version: string;
}

export interface PlacementComponentLike {
  id: string;
  versions: PlacementComponentVersionLike[];
}

export interface PlacementDraftDefaults {
  componentId: string;
  componentVersion: string;
  sampleContext: string;
  sampleProps: string;
}

export interface PlacementDraftFormState extends PlacementDraftDefaults {
  changeNote: string;
  description: string;
  placementKey: string;
  model: string;
  name: string;
  promptTemplate: string;
  status: 'active' | 'draft';
}

export function buildDefaultPlacementDraftState(
  components: PlacementComponentLike[],
): PlacementDraftDefaults {
  const firstComponent = components[0] || null;
  const firstVersion = firstComponent?.versions[0] || null;

  return {
    componentId: firstComponent?.id || '',
    componentVersion: firstVersion?.version || '',
    sampleContext: firstVersion
      ? JSON.stringify(firstVersion.defaultContext, null, 2)
      : DEFAULT_PLACEMENT_SAMPLE_CONTEXT,
    sampleProps: firstVersion
      ? JSON.stringify(firstVersion.defaultProps, null, 2)
      : DEFAULT_PLACEMENT_SAMPLE_PROPS,
  };
}

/** The create form's working prompt, so a new draft is testable before it is rewritten. */
export function buildInitialCreatePlacementPromptTemplate(
  name: string,
  placementKey: string,
): string {
  return `Draft prompt for ${name} (${placementKey}). Update this prompt template before setting status to active.`;
}

/** Suggests a placement key from a display name; the person can still edit it. */
export function placementKeyFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 64);
}

/** A readable name for a placement that has only a key (duplicate and create prefill). */
export function placementNameFromKey(placementKey: string): string {
  return placementKey
    .split(/[._-]+/)
    .filter(Boolean)
    .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(' ');
}
